import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import VoihuMark from "@/components/VoihuMark";

export default function Nav() {
  const { user, logout, requireAuth } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const hide = location.pathname.includes("/impact");
  const [notifs, setNotifs] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!user) { setNotifs([]); return; }
    let cancelled = false;
    const load = async () => {
      try {
        const r = await api.get("/notifications");
        if (!cancelled) setNotifs(r.data || []);
      } catch (_) { /* ignore */ }
    };
    load();
    const t = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, [user, location.pathname]);

  const unread = notifs.filter((n) => !n.read).length;

  const openList = async () => {
    const willOpen = !open;
    setOpen(willOpen);
    if (willOpen) {
      try { await api.post("/notifications/mark-read"); } catch (_) {}
      // Re-fetch so read:true is reflected even if the initial fetch was slow.
      try {
        const r = await api.get("/notifications");
        setNotifs(r.data || []);
      } catch (_) { /* ignore */ }
    }
  };

  return (
    <header className={`sticky top-0 z-40 bg-[color:var(--oh-bg)]/95 backdrop-blur-sm border-b oh-hairline ${hide ? 'hidden' : ''}`}
            data-testid="nav-header">
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-5 flex items-center justify-between">
        <Link to="/" className="oh-label" data-testid="nav-logo">
          <VoihuMark testid="nav-logo-mark" />
        </Link>
        <nav className="flex items-center gap-6 sm:gap-8">
          <Link to="/why-voihu" className="oh-label hover:text-[color:var(--oh-ink)] transition-colors hidden sm:inline"
                data-testid="nav-why-voihu">
            Why VOIHU
          </Link>
          <Link to="/explore" className="oh-label hover:text-[color:var(--oh-ink)] transition-colors"
                data-testid="nav-explore">
            Explore
          </Link>
          <button
            onClick={() => requireAuth(() => navigate("/create"))}
            className="oh-label hover:text-[color:var(--oh-ink)] transition-colors"
            data-testid="nav-start">
            Start
          </button>
          {user && (
            <div className="relative">
              <button onClick={openList}
                      className="oh-label hover:text-[color:var(--oh-red)] transition-colors relative"
                      data-testid="nav-notifications">
                Heard
                {unread > 0 && (
                  <span className="absolute -top-2 -right-3 min-w-[16px] h-4 px-1 flex items-center justify-center bg-[color:var(--oh-red)] text-white text-[10px] rounded-full"
                        data-testid="nav-notif-badge">
                    {unread}
                  </span>
                )}
              </button>
              {open && (
                <div className="absolute right-0 mt-3 w-80 bg-[color:var(--oh-bg)] border oh-hairline shadow-lg z-50"
                     data-testid="nav-notif-panel">
                  <div className="p-4 border-b oh-hairline oh-label">Notifications</div>
                  {notifs.length === 0 ? (
                    <div className="p-6 text-sm text-[color:var(--oh-ink-3)]">Nothing yet.</div>
                  ) : (
                    <ul className="max-h-80 overflow-auto">
                      {notifs.slice(0, 10).map((n) => (
                        <li key={n.id} className="border-b oh-hairline last:border-b-0">
                          <Link to={`/message/${n.message_id}`} onClick={() => setOpen(false)}
                                className="block px-4 py-3 hover:bg-[color:var(--oh-bg-2)]"
                                data-testid="nav-notif-item">
                            <div className="oh-label text-[color:var(--oh-red)]">Heard ✓</div>
                            {n.message_title && (
                              <div className="font-serif-editorial text-lg mt-1 leading-tight text-[color:var(--oh-ink)]"
                                   data-testid="nav-notif-title">
                                {n.message_title}
                              </div>
                            )}
                            <div className="text-sm mt-1 text-[color:var(--oh-ink-2)]">
                              The MESSAGE you supported has been heard.
                            </div>
                            <div className="oh-label mt-1 text-[color:var(--oh-ink-3)]">
                              {new Date(n.created_at).toLocaleString()}
                            </div>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          )}
          {user ? (
            <button onClick={logout} className="oh-label hover:text-[color:var(--oh-red)] transition-colors"
                    data-testid="nav-logout">
              Sign out
            </button>
          ) : (
            <button onClick={() => requireAuth(() => {})}
                    className="oh-label hover:text-[color:var(--oh-ink)] transition-colors"
                    data-testid="nav-signin">
              Sign in
            </button>
          )}
        </nav>
      </div>
    </header>
  );
}
